import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { FiPhone, FiMapPin, FiChevronRight, FiCheckCircle } from "react-icons/fi";

const lagosLGAs = [
  "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa",
  "Badagry", "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye",
  "Ikeja", "Ikorodu", "Kosofe", "Lagos Island", "Lagos Mainland",
  "Mushin", "Ojo", "Oshodi-Isolo", "Shomolu", "Surulere",
];

const CompleteProfile = ({ refreshAuth }) => {
  const [phone, setPhone] = useState("");
  const [lga, setLga] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || localStorage.getItem("userEmail");

  const handleSubmit = async () => {
    if (!phone || !lga) {
      setMessage("⚠ Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      await api.CompleteProfile({
        email,
        phone,
        lga,
      });

      setMessage("✅ Profile updated successfully!");

      // Specific navigation logic preserved
      setTimeout(() => {
        navigate("/confirm-email", {
          state: { email: email },
        });
      }, 1500);
      
    } catch (error) {
      setMessage("❌ Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      {/* Decorative Circles */}
      <div className="absolute top-[-5%] left-[-5%] w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-[-5%] right-[-5%] w-72 h-72 bg-blue-50 rounded-full blur-3xl opacity-40"></div>

      {/* Header */}
      <div className="p-6 relative z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-900 group"
        >
          <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-orange-50 transition-colors">
            <IoMdArrowBack size={20} />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Back</span>
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] p-8 shadow-2xl shadow-gray-200/50 border border-gray-50">
          
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle size={32} />
            </div>
            <h2 className="text-3xl font-black tracking-tighter text-gray-900 mb-2">
              Final Steps
            </h2>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
              Complete your profile to unlock <br /> full marketplace access
            </p>
          </div>

          <div className="space-y-6">
            {/* Phone Input */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">
                Phone Number
              </label>
              <div className="relative">
                <FiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <input
                  type="number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone Number"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold"
                />
              </div>
            </div>

            {/* LGA Dropdown */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">
                Primary Location (LGA)
              </label>
              <div className="relative">
                <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <select
                  value={lga}
                  onChange={(e) => setLga(e.target.value)}
                  className="w-full pl-12 pr-10 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold appearance-none cursor-pointer"
                >
                  <option value="">Select your LGA</option>
                  {lagosLGAs.map((item, idx) => (
                    <option key={idx} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                   <FiChevronRight className="text-gray-400 rotate-90" />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-black transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Save & Continue
                  <FiChevronRight size={18} />
                </>
              )}
            </button>

            {/* Message Feedback */}
            {message && (
              <div className={`p-4 rounded-xl border text-[10px] font-black uppercase text-center tracking-wider animate-pulse ${
                message.includes("✅") 
                ? "bg-green-50 border-green-100 text-green-600" 
                : "bg-red-50 border-red-100 text-red-600"
              }`}>
                {message}
              </div>
            )}
          </div>

          <p className="mt-8 text-center text-[9px] font-bold text-gray-400 uppercase leading-loose">
            Your location helps us show you <br /> the most relevant sellers and riders nearby.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;